/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { Colors, ColorValue } from '../../../theme/Colors';
import { Typography } from '../../../theme/Typography';
import { Text } from '../../display/Text';
import { Img } from '../../display/Img/Img';

type BackgroundColors =
  | 'cream'
  | 'neutral100'
  | 'acidWash'
  | 'neutral200'
  | 'lava10'
  | 'ocean10'
  | 'sun10'
  | 'white';

export type Props = {
  /**
   * Controls the background of the entire element.
   * Must be one of type `'cream' | 'neutral100' | 'acidWash' | 'neutral200' | 'lava10' | 'ocean10' | 'sun10' | 'white'`.
   */
  backgroundColor?: BackgroundColors;
  /**
   * Heading text content. Hidden when imageUrl is provided.
   */
  heading?: React.ReactNode;
  /**
   * Heading tag. Defines the HTML tag for the heading that component will render.
   */
  headingTag?: 'h1' | 'h2' | 'h3';
  /**
   * Text content displayed below the heading. Hidden when imageUrl is provided.
   */
  description?: React.ReactNode;
  /**
   * Image URL. Used to render an Img component.
   */
  imageUrl?: string;
  /**
   * Image alt text.
   */
  altText?: string;
  /**
   * Text textAlign options.
   */
  textAlign?: 'left' | 'right' | 'center' | 'inherit';
};

const breakpoints = {
  tablet: 768,
  desktop: 1230,
};

const mapV4ToV5Color = (color: ColorValue): ColorValue => {
  const colorMap = {
    [Colors.lava10]: Colors.cream,
    [Colors.ocean10]: Colors.acidWash,
    [Colors.sun10]: Colors.neutral200,
    [Colors.white]: Colors.neutral100,
  };

  type keys = keyof typeof colorMap;

  return colorMap[color as keys] || color;
};

const Styles = labelStyles('OneColumnBlock', {
  root: (backgroundColor: Props['backgroundColor'] = 'neutral100') => [
    css`
      background-color: ${mapV4ToV5Color(Colors[backgroundColor])};
      padding: 20px;
      @media screen and (min-width: ${breakpoints.tablet}px) {
        padding: 40px 30px;
      }
      @media screen and (min-width: ${breakpoints.desktop}px) {
        padding: 50px 30px;
      }
    `,
  ],
  content: [
    css`
      margin: 0 auto;
      text-align: center;
      max-width: 1170px;
    `,
  ],
  text: (textAlign: string) =>
    css`
      text-align: ${textAlign};
      max-width: 720px;
      margin-left: ${textAlign === 'center' || textAlign === 'right'
        ? 'auto'
        : 0};
      margin-right: ${textAlign === 'center' ? 'auto' : 0};
    `,
  headingContainer: [
    css`
      ${Typography.pageHeader5}

      @media screen and (min-width: ${breakpoints.desktop}px) {
        ${Typography.pageHeader5}
      }
    `,
  ],
  description: [
    css`
      margin-top: 20px;
      ${Typography.smallPrint1}
      color: ${Colors.neutral700};

      @media screen and (min-width: ${breakpoints.tablet}px) {
        ${Typography.paragraph1Short}
      }
    `,
  ],
});

export const OneColumnBlock = ({
  backgroundColor,
  altText = '',
  headingTag = 'h3',
  heading,
  description,
  imageUrl,
  textAlign = 'center',
}: Props) => {
  return (
    <div css={Styles.root(backgroundColor)}>
      <div css={Styles.content}>
        {imageUrl ? (
          <Img borderRadius={0} src={imageUrl} alt={altText} />
        ) : (
          <div data-testid="Text" css={Styles.text(textAlign)}>
            <div css={Styles.headingContainer}>
              <Text type="heading" color="neutral800" tag={headingTag}>
                {heading}
              </Text>
            </div>
            {description && <div css={Styles.description}>{description}</div>}
          </div>
        )}
      </div>
    </div>
  );
};
